import { useState } from 'react';
import { usePoses, setRuntimePoses, tierLabel } from '../data/poses';
import { api } from '../api/client';
import type { ProgressApi } from '../hooks/useProgress';
import { DEFAULT_HOLD_SECONDS, type RoutinesApi } from '../hooks/useRoutines';
import { navigate } from '../router';
import { PoseMedia } from './PoseMedia';
import { StatusControl } from './StatusControl';
import { PoseImageEditor } from './PoseImageEditor';
import { SuggestButton } from './FeedbackPanel';
import { useAuth } from '../auth/AuthContext';

interface Props {
  id: string;
  progress: ProgressApi;
  routines: RoutinesApi;
}

export function PoseDetailPage({ id, progress, routines }: Props) {
  const poses = usePoses();
  const pose = poses.find((p) => p.id === id);
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [addedTo, setAddedTo] = useState<string | null>(null);

  const deletePose = async () => {
    if (!pose) return;
    if (!window.confirm(`Delete "${pose.nameEnglish}"? This can't be undone.`)) return;
    await api.deletePose(pose.id);
    const list = await api.listPoses();
    setRuntimePoses(list);
    navigate('/');
  };

  if (!pose) {
    return (
      <div className="empty">
        <p>Pose “{id}” not found.</p>
        <a className="btn" href="#/">
          ← Back to catalog
        </a>
      </div>
    );
  }

  const index = poses.findIndex((p) => p.id === pose.id);
  const prev = index > 0 ? poses[index - 1] : undefined;
  const next = index < poses.length - 1 ? poses[index + 1] : undefined;

  const addToRoutine = (routineId: string, routineName: string) => {
    const routine = routines.get(routineId);
    if (!routine) return;
    routines.update(routineId, {
      steps: [...routine.steps, { poseId: pose.id, holdSeconds: DEFAULT_HOLD_SECONDS }],
    });
    setMenuOpen(false);
    setAddedTo(routineName);
    window.setTimeout(() => setAddedTo(null), 2500);
  };

  const newRoutineWithPose = () => {
    const routine = routines.create('', [
      { poseId: pose.id, holdSeconds: DEFAULT_HOLD_SECONDS },
    ]);
    navigate(`/routines/${routine.id}/edit`);
  };

  return (
    <article className="detail">
      <a className="back" href="#/">
        ← Catalog
      </a>

      <div className="detail-grid">
        <div className="detail-media">
          <PoseMedia pose={pose} />
          {user?.isAdmin && <PoseImageEditor pose={pose} />}
          {user?.isAdmin && pose.custom && (
            <button type="button" className="btn danger delete-pose" onClick={deletePose}>
              Delete this pose
            </button>
          )}
        </div>

        <div className="detail-info">
          <div className="titles">
            <h1>{pose.nameEnglish}</h1>
            {pose.nameSanskrit && (
              <div className="sanskrit lg">
                {pose.nameSanskrit}
                {pose.sanskritFromSource === false && (
                  <span className="flag"> · not named in source</span>
                )}
              </div>
            )}
          </div>

          <div className="meta">
            <span className="chip">{pose.category}</span>
            <span className={`chip level tier-${pose.difficultyTier}`}>
              {tierLabel(pose.difficultyTier)}
            </span>
            {pose.hold && <span className="chip hold">{pose.hold}</span>}
          </div>

          <div className="detail-actions">
            <StatusControl
              value={progress.statusOf(pose.id)}
              onChange={(s) => progress.setStatus(pose.id, s)}
            />

            <div className="add-routine">
              <button
                type="button"
                className="btn"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
              >
                + Add to routine
              </button>
              {menuOpen && (
                <div className="add-menu" role="menu">
                  {routines.routines.length > 0 && (
                    <>
                      <div className="add-menu-label">Add to existing</div>
                      {routines.routines.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          className="add-menu-item"
                          role="menuitem"
                          onClick={() => addToRoutine(r.id, r.name)}
                        >
                          {r.name}
                          <span className="add-menu-count">{r.steps.length}</span>
                        </button>
                      ))}
                      <div className="add-menu-divider" />
                    </>
                  )}
                  <button
                    type="button"
                    className="add-menu-item new"
                    role="menuitem"
                    onClick={newRoutineWithPose}
                  >
                    + New routine with this pose
                  </button>
                </div>
              )}
            </div>

            {addedTo && (
              <span className="added-toast" role="status">
                Added to “{addedTo}” ✓
              </span>
            )}
          </div>

          <p className="desc lg">{pose.description}</p>

          {pose.benefits.length > 0 && (
            <section>
              <h3 className="section-label">Benefits</h3>
              <div className="benefits">
                {pose.benefits.map((b) => (
                  <span className="benefit" key={b}>
                    {b}
                  </span>
                ))}
              </div>
            </section>
          )}

          {pose.cues && pose.cues.length > 0 && (
            <section>
              <h3 className="section-label">How to</h3>
              <ol className="cues">
                {pose.cues.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ol>
            </section>
          )}

          {pose.modification && (
            <div className="modify">
              <b>Modify:</b> {pose.modification}
            </div>
          )}
        </div>
      </div>

      <div className="pose-suggest">
        <SuggestButton pose={pose} label="Suggest a fix for this pose" />
      </div>

      <nav className="detail-nav">
        {prev ? (
          <a className="btn" href={`#/pose/${prev.id}`}>
            ← {prev.nameEnglish}
          </a>
        ) : (
          <span />
        )}
        {next ? (
          <a className="btn" href={`#/pose/${next.id}`}>
            {next.nameEnglish} →
          </a>
        ) : (
          <span />
        )}
      </nav>
    </article>
  );
}
